import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Award, Banknote, Folder, Mail, Pencil, Phone, Trash2, TrendingUp } from "lucide-react";

import { api } from "@/lib/api";
import { DossierItem } from "@/components/dossier-item";
import { pct } from "@/components/masters";
import { Header } from "@/components/header";
import { Badge, Button, Card, ErrorView, IconButton, Loading, Row, Stat } from "@/components/kit";
import { Sheet } from "@/components/sheet";
import { useToast } from "@/components/toast";
import { C, date, dateTime, eur } from "@/lib/format";

function Info({ m }) {
  return (
    <>
      <Card>
        <h2 className="t-h3 mb-2">Entreprise &amp; coordonnées</h2>
        <Row label="SIRET" value={m.siret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, "$1 $2 $3 $4")} testID="master-siret" />
        <Row label="Interlocuteur" value={m.contact_name} />
        <Row label="Téléphone" value={m.phone} testID="master-phone" />
        <Row label="E-mail" value={m.email} />
        <Row label="Adresse" value={m.address} />
        <Row label="Ville" value={m.city} />
        <Row label="Siège" value={`${m.department} - ${m.department_name}`} />
        <div className="mt-3 flex gap-2">
          {m.phone ? <a href={`tel:${m.phone}`} data-testid="master-call-button" className="focus-ring flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-[#555555] bg-[#2A2A2A] font-bold text-white hover:border-[#D4AF37]/70"><Phone className="h-[18px] w-[18px]" />Appeler</a> : null}
          {m.email ? <a href={`mailto:${m.email}`} data-testid="master-mail-button" className="focus-ring flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-[#555555] bg-[#2A2A2A] font-bold text-white hover:border-[#D4AF37]/70"><Mail className="h-[18px] w-[18px]" />E-mail</a> : null}
        </div>
      </Card>
      <Card className="flex flex-col gap-2">
        <h2 className="t-h3">Départements couverts</h2>
        <div className="flex flex-wrap gap-2">{m.departments_covered.map((c) => <Badge key={c} label={c} color={C.brand} />)}</div>
        <h2 className="t-h3 mt-2">Spécialités</h2>
        <p className="t-body" data-testid="master-specialties">{m.specialties.length ? m.specialties.join(" · ") : "—"}</p>
      </Card>
      <Card>
        <h2 className="t-h3 mb-2">Assurances &amp; commission</h2>
        <Row label="Garantie décennale" value={m.insurance_decennale ? "Vérifiée" : "Non vérifiée"} />
        <Row label="RC professionnelle" value={m.insurance_rc_pro ? "Vérifiée" : "Non vérifiée"} />
        <Row label="Échéance" value={m.insurance_expiry ? date(m.insurance_expiry) : null} />
        <Row label="Vérifiées le" value={m.insurance_verified_at ? date(m.insurance_verified_at) : null} />
        <Row label="Assureur / police" value={m.insurance_notes} />
        <Row label="Commission négociée (indicative)" value={pct(m.commission_rate)} testID="master-commission-rate" />
        {m.notes ? <p className="t-body mt-2 whitespace-pre-line">{m.notes}</p> : null}
      </Card>
    </>
  );
}

function Projects({ m }) {
  return (
    <>
      <section className="flex flex-col gap-3">
        <h2 className="t-h3">Projets attribués en cours ({m.current_projects.length})</h2>
        {m.current_projects.length ? m.current_projects.map((d) => <DossierItem key={d.id} d={d} admin />) : <p className="t-caption">Aucun projet en cours.</p>}
      </section>
      <section className="flex flex-col gap-3">
        <h2 className="t-h3">Historique des collaborations ({m.past_projects.length})</h2>
        {m.past_projects.length ? m.past_projects.map((d) => (
          <Link key={d.id} to={`/admin/dossier/${d.id}`} data-testid={`master-past-${d.number}`} className="pressable card flex flex-col gap-0.5">
            <span className="flex justify-between"><span className="t-caption !text-[#D4AF37]">{d.number}</span><span className="t-caption">{date(d.created_at)}</span></span>
            <span className="t-h3 truncate">{d.client.first_name} {d.client.last_name} · {d.city}</span>
            <span className="t-caption">Signé {eur(d.contract_amount)} · commission {d.master_commission_amount !== null && d.master_commission_amount !== undefined ? eur(d.master_commission_amount) : "à définir"}</span>
          </Link>
        )) : <p className="t-caption">Aucun contrat signé pour l'instant.</p>}
      </section>
      <Card className="flex flex-col gap-2">
        <h2 className="t-h3">Journal</h2>
        {m.history.map((h, i) => (
          <div key={i} className="flex gap-2">
            <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#D4AF37]" />
            <div className="flex-1"><p className="text-sm text-white">{h.action}</p><p className="t-caption">{h.by} · {dateTime(h.at)}</p></div>
          </div>
        ))}
      </Card>
    </>
  );
}

export default function MasterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-master", id], queryFn: () => api(`/admin/masters/${id}`) });
  const [delOpen, setDelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const m = q.data;

  const remove = async () => {
    setBusy(true);
    try {
      await api(`/admin/masters/${id}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["admin-masters"] });
      qc.invalidateQueries({ queryKey: ["admin-geo"] });
      toast("Fiche supprimée.");
      setDelOpen(false);
      navigate(-1);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="master-detail-screen" className="flex flex-col">
      <Header title={m?.company_name ?? "Maître d'œuvre"} subtitle={m ? `${m.city} · ${m.region}` : undefined}
        right={m ? <IconButton icon={Pencil} label="Modifier la fiche" testID="master-edit-button" onClick={() => navigate(`/admin/master-form?id=${id}`)} /> : undefined} />
      {q.isLoading ? <Loading /> : q.isError || !m || !m.totals ? <ErrorView message="Fiche introuvable." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4 pb-8 md:p-8">
          <div className="flex flex-wrap gap-2">
            <Badge testID="master-insurance-badge" label={m.insurance_ok ? "Assurances vérifiées" : "Assurances à vérifier"} color={m.insurance_ok ? C.success : C.warning} />
            <Badge label={m.status === "active" ? "Partenaire actif" : "Désactivé"} color={m.status === "active" ? C.brand : C.silver} />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat testID="master-stat-projects" icon={Folder} label="Projets attribués" value={m.totals.projects} />
            <Stat testID="master-stat-signed" icon={Award} label="Contrats signés" value={m.totals.signed} accent={C.success} />
            <Stat testID="master-stat-amount" icon={Banknote} label="Montant signé" value={eur(m.totals.signed_amount)} />
            <Stat testID="master-stat-commission" icon={TrendingUp} label="Commissions (dossiers signés)" value={eur(m.totals.commission_amount)} accent={C.success} />
          </div>
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-4"><Info m={m} /></div>
            <div className="flex flex-col gap-4">
              <Projects m={m} />
              {m.totals.projects === 0 && <Button testID="master-delete-button" title="Supprimer la fiche" variant="danger" icon={Trash2} onClick={() => setDelOpen(true)} />}
            </div>
          </div>
        </div>
      )}
      <Sheet visible={delOpen} onClose={() => setDelOpen(false)} title="Supprimer ce maître d'œuvre ?" testID="master-delete-sheet">
        <div className="flex flex-col gap-4">
          <p className="t-body">La fiche sera définitivement supprimée. Cette action est réservée à l'administrateur.</p>
          <Button testID="master-delete-confirm" title="Supprimer" variant="danger" loading={busy} onClick={remove} />
        </div>
      </Sheet>
    </div>
  );
}
